// 1:1 port of app/login.html — 2-column sign-in (brand panel + sign-in form).
// Uses position:fixed to escape the body grid layout so it fills the viewport
// regardless of any sidebar grid placement.

import { useState, useEffect } from 'react'
import { signInWithEmailAndPassword } from 'firebase/auth'
import { firebaseAuth } from '../lib/firebase'
import { getBrand, applyBrandToDocument } from '../lib/brand'

export function Login() {
  const brand = getBrand()
  useEffect(() => { applyBrandToDocument(brand) }, [brand])
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function emailSignIn(e: React.FormEvent) {
    e.preventDefault()
    setError(''); setBusy(true)
    try {
      await signInWithEmailAndPassword(firebaseAuth, email.trim(), password)
    } catch (err) {
      const msg = (err as Error).message || 'Sign-in failed'
      // Friendlier error mapping for common cases
      if (msg.includes('user-not-found') || msg.includes('wrong-password') || msg.includes('invalid-credential')) {
        setError('Incorrect email or password.')
      } else if (msg.includes('invalid-email')) {
        setError('Please enter a valid email.')
      } else {
        setError(msg)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, background: 'var(--bg-app)', fontFamily: 'var(--font-body)',
    }}>
      <div style={{
        width: '100%', maxWidth: 960,
        display: 'grid', gridTemplateColumns: '1fr 1fr',
        background: 'var(--bg-panel)', borderRadius: 16,
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid var(--border)',
        overflow: 'hidden',
      }} className="login-grid">
        {/* Brand panel */}
        <div style={{ padding: '60px 40px', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }} className="login-brand-side">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 40 }}>
            {brand.logoAspect === 'wide' ? (
              <img src={brand.logo} alt={brand.name} style={{ height: 48, width: 'auto' }} />
            ) : (
              <>
                <img src={brand.logo} alt={brand.name} style={{ width: 48, height: 48, borderRadius: 10 }} />
                <span style={{ fontFamily: 'Outfit, var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--text-main)' }}>{brand.name}</span>
              </>
            )}
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', fontWeight: 800, marginBottom: 16, color: 'var(--text-main)' }}>
            The AI-native marketing intelligence platform.
          </h1>
          <p style={{ fontSize: '1rem', lineHeight: 1.7, marginBottom: 32, color: 'var(--text-muted)' }}>
            Attribution, MMM, incrementality, experimentation, and creative intelligence unified in one system.
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {[
              'Experiments + Ghost Incrementality measurement',
              'Patent-pending Creative Intelligence',
              'AI agents for autonomous optimization',
              'True ROAS measurement across channels',
              'Real-time insights and anomaly detection',
            ].map((t) => (
              <li key={t} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, fontSize: '0.95rem', fontWeight: 500, color: 'var(--text-main)' }}>
                <span style={{
                  width: 20, height: 20, background: 'var(--bg-element)', borderRadius: 6, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, color: 'var(--text-main)',
                }}>✓</span>
                {t}
              </li>
            ))}
          </ul>
        </div>

        {/* Form panel */}
        <div style={{ padding: '60px 50px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-main)', marginBottom: 8 }}>
            Sign in
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: 32 }}>
            Sign in with your email and password.
          </p>

          {error && (
            <div style={{
              background: 'var(--danger-soft)', border: '1px solid rgba(220,38,38,0.2)',
              color: 'var(--danger)', padding: '12px 16px', borderRadius: 'var(--radius)',
              marginBottom: 20, fontSize: '0.875rem',
            }}>{error}</div>
          )}

          <form onSubmit={emailSignIn}>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: 'var(--text-main)', fontSize: '0.875rem' }}>Email</label>
              <input type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required
                     style={{
                       width: '100%', padding: '14px 16px', border: '1px solid var(--border)',
                       borderRadius: 'var(--radius)', fontSize: 15, fontFamily: 'var(--font-body)',
                       background: 'var(--bg-panel)', color: 'var(--text-main)',
                     }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: 'var(--text-main)', fontSize: '0.875rem' }}>Password</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input type={showPw ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required
                       style={{
                         width: '100%', padding: '14px 48px 14px 16px', border: '1px solid var(--border)',
                         borderRadius: 'var(--radius)', fontSize: 15, fontFamily: 'var(--font-body)',
                         background: 'var(--bg-panel)', color: 'var(--text-main)',
                       }} />
                <button type="button" onClick={() => setShowPw(!showPw)}
                        style={{ position: 'absolute', right: 12, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
                  {showPw ? '🙈' : '👁'}
                </button>
              </div>
            </div>
            <button type="submit" disabled={busy} style={{
              width: '100%', background: 'var(--accent)', color: '#0a0a0a', border: 'none',
              padding: 14, borderRadius: 'var(--radius)', fontSize: 15, fontWeight: 700,
              fontFamily: 'var(--font-display)', cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.7 : 1,
            }}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .login-grid { grid-template-columns: 1fr !important; max-width: 420px; }
          .login-brand-side { display: none !important; }
        }
      `}</style>
    </div>
  )
}
